import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useLoader } from "@/hooks/LoaderProvider";
import { ArrowRightOutlined } from "@/config/antd";
import { Checkbox } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import Button from "@/components/common/Button";
import Back from "@/components/specific/Back";
import Success from "@/components/common/alerts/Success";
import WebIcon from "@/components/common/Web_Icons";
import {
  setCurrentStep,
  createRole,
  selectRoleFormData,
  resetRoleFormData,
  selectSingleRole,
} from "@/store/redux/slices/roles/role_slice";
import { getOrganizationId } from "@/utils/token_functions";
import { fetchUsersByAny } from "@/store/redux/slices/user_admin/user_admin";

const AddRolesStep3 = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formData = useSelector(selectRoleFormData);
  const singleRole = useSelector(selectSingleRole);
  const { id } = useParams();

  const [selectedUsersData, setSelectedUsersData] = useState([]);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const isEditMode = Boolean(id);

  useEffect(() => {
    const userIds = Array.isArray(formData.userIDs) ? formData.userIDs : [];
    if (userIds.length === 0) {
      setSelectedUsersData([]);
      return;
    }

    const idSet = new Set(userIds.map(String));

    const roleUsers = Array.isArray(singleRole?.users)
      ? singleRole.users
          .flatMap((entry) => (Array.isArray(entry) ? entry : [entry]))
          .filter(
            (user) =>
              user &&
              typeof user === "object" &&
              idSet.has(String(user._id || user.id))
          )
      : [];

    if (roleUsers.length === userIds.length) {
      setSelectedUsersData(roleUsers);
      return;
    }

    let cancelled = false;

    const loadOrgUsers = async () => {
      const organizationID = getOrganizationId();
      if (!organizationID) {
        setSelectedUsersData(roleUsers);
        return;
      }

      showLoader();
      try {
        const result = await dispatch(
          fetchUsersByAny({ key: "organizationID", value: organizationID })
        ).unwrap();
        if (cancelled) return;

        const orgUsersRaw = result?.data;
        const orgUsers = Array.isArray(orgUsersRaw?.result)
          ? orgUsersRaw.result
          : Array.isArray(orgUsersRaw)
            ? orgUsersRaw
            : [];
        const matched = orgUsers.filter((user) =>
          idSet.has(String(user._id || user.id))
        );
        setSelectedUsersData(matched.length > 0 ? matched : roleUsers);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching users for role review:", error);
          setSelectedUsersData(roleUsers);
        }
      } finally {
        if (!cancelled) hideLoader();
      }
    };

    loadOrgUsers();

    return () => {
      cancelled = true;
    };
  }, [dispatch, formData.userIDs, hideLoader, showLoader, singleRole?.users]);

  const handleBack = () => {
    dispatch(setCurrentStep(2));
    navigate(
      isEditMode
        ? `/dashboard/roles/edit/${id}/step-2`
        : "/dashboard/roles/add-roles-step-2"
    );
  };

  const handleCloseSuccess = () => {
    navigate("/dashboard/roles");
    setIsSuccessVisible(false);
  };

  const handleProceed = async () => {
    showLoader();
    const organizationID = getOrganizationId();
    try {
      const roleData = {
        id: isEditMode ? formData.roleId : undefined,
        roleName: formData.roleName,
        name: formData.roleName,
        description: formData.description,
        userIDs: formData.userIDs,
        permissions: formData.permissions,
        organizationID: organizationID,
      };

      const result = await dispatch(createRole(roleData)).unwrap();
      if (result) {
        dispatch(resetRoleFormData());
        setIsSuccessVisible(true);
      }
    } catch (error) {
      console.error("Failed to create role:", error);
    } finally {
      hideLoader();
    }
  };

  const isPermissionSelected = (permissionKey) => {
    return formData.permissions?.includes(permissionKey) || false;
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <Back />
      </div>
      <div className="flex-row-reverse gap-40 md:flex">
        <div className="w-full lg:border-2 border-[#eee] lg:p-3 rounded-lg">
          <h1 className="mb-2 text-left heading-medium sm:text-3xl">
            Review Role Details
          </h1>

          {/* Role Details Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h1 className="font-bold text-medium">Role Name</h1>
              <h1 className="text-medium">{formData.roleName}</h1>
            </div>
            <div className="flex flex-col gap-2">
              <h1 className="font-bold text-medium">Role Description</h1>
              <p className="font-normal text-small">{formData.description}</p>
            </div>
          </div>

          {/* Selected Users Section */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <WebIcon icon="UserGroupedFilled" size="small" />
              <h1 className="font-bold text-regular">Selected Users</h1>
            </div>
            <div className="flex flex-wrap gap-3">
              {selectedUsersData.map((user) => (
                <div
                  key={user._id}
                  className="flex items-center gap-2 p-3 rounded-sm bg-secondary">
                  <Avatar src={user.profileImage} />
                  <p className="font-bold text-regular">{user.fullname}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Permissions Section */}
          <div className="space-y-6">
            <h1 className="font-bold text-regular">Selected Permissions</h1>

            {/* User Management */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">User Management</h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage users
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-small">Create Users</span>
                  <Checkbox
                    checked={isPermissionSelected("UserModelCreate")}
                    disabled
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-small">Update Users</span>
                  <Checkbox
                    checked={isPermissionSelected("UserModelUpdate")}
                    disabled
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-small">Read Users</span>
                  <Checkbox
                    checked={isPermissionSelected("UserModelRead")}
                    disabled
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-small">Delete Users</span>
                  <Checkbox
                    checked={isPermissionSelected("UserModelDelete")}
                    disabled
                  />
                </div>
              </div>
            </div>

            {/* Role Management */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">Role Management</h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage roles and assignments
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-small">Create Roles</span>
                  <Checkbox
                    checked={isPermissionSelected("RoleModelCreate")}
                    disabled
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-small">Update Roles</span>
                  <Checkbox
                    checked={isPermissionSelected("RoleModelUpdate")}
                    disabled
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-small">Read Roles</span>
                  <Checkbox
                    checked={isPermissionSelected("RoleModelRead")}
                    disabled
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-small">Delete Roles</span>
                  <Checkbox
                    checked={isPermissionSelected("RoleModelDelete")}
                    disabled
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-small">Assign Roles</span>
                  <Checkbox
                    checked={isPermissionSelected("AssignRoleModelCreate")}
                    disabled
                  />
                </div>
              </div>
            </div>

            {/* Organization & Account */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">
                  Organization & Account
                </h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage organizations and accounts
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["Create", "Update", "Read", "Delete"].map((action) => (
                  <React.Fragment key={`org-${action}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Organizations`}</span>
                      <Checkbox
                        checked={isPermissionSelected(
                          `OrganizationModel${action}`
                        )}
                        disabled
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Accounts`}</span>
                      <Checkbox
                        checked={isPermissionSelected(`AccountModel${action}`)}
                        disabled
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Authentication & Security */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">
                  Authentication & Security
                </h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage authentication and security
                  settings
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["Create", "Update", "Read", "Delete"].map((action) => (
                  <React.Fragment key={`auth-${action}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Authenticators`}</span>
                      <Checkbox
                        checked={isPermissionSelected(
                          `AuthenticatorModel${action}`
                        )}
                        disabled
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} OTP`}</span>
                      <Checkbox
                        checked={isPermissionSelected(`OTPModel${action}`)}
                        disabled
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Recovery Questions`}</span>
                      <Checkbox
                        checked={isPermissionSelected(
                          `RecoveryQuestionModel${action}`
                        )}
                        disabled
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Event Management */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">Event Management</h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage events and related activities
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["Create", "Update", "Read", "Delete"].map((action) => (
                  <React.Fragment key={`event-${action}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Events`}</span>
                      <Checkbox
                        checked={isPermissionSelected(`EventModel${action}`)}
                        disabled
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Sub-Events`}</span>
                      <Checkbox
                        checked={isPermissionSelected(`SubEventModel${action}`)}
                        disabled
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Townhalls`}</span>
                      <Checkbox
                        checked={isPermissionSelected(`TownhallModel${action}`)}
                        disabled
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Financial Management */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">
                  Financial Management
                </h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage financial aspects
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["Create", "Update", "Read", "Delete"].map((action) => (
                  <React.Fragment key={`financial-${action}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Expenses`}</span>
                      <Checkbox
                        checked={isPermissionSelected(`ExpenseModel${action}`)}
                        disabled
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Campaign Funds`}</span>
                      <Checkbox
                        checked={isPermissionSelected(
                          `CampaignFundsModel${action}`
                        )}
                        disabled
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Donations`}</span>
                      <Checkbox
                        checked={isPermissionSelected(`DonationModel${action}`)}
                        disabled
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Communication & Media */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">
                  Communication & Media
                </h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage communication and media content
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["Create", "Update", "Read", "Delete"].map((action) => (
                  <React.Fragment key={`comm-${action}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Surveys/Polls`}</span>
                      <Checkbox
                        checked={isPermissionSelected(
                          `SurveyPollsModel${action}`
                        )}
                        disabled
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Media`}</span>
                      <Checkbox
                        checked={isPermissionSelected(`MediaModel${action}`)}
                        disabled
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Support & Content */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">
                  Support & Content
                </h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage support and content
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["Create", "Update", "Read", "Delete"].map((action) => (
                  <React.Fragment key={`support-${action}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Support Tickets`}</span>
                      <Checkbox
                        checked={isPermissionSelected(
                          `SupportTicketModel${action}`
                        )}
                        disabled
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} FAQs`}</span>
                      <Checkbox
                        checked={isPermissionSelected(`FaqModel${action}`)}
                        disabled
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Candidate & Campaign */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">
                  Candidate & Campaign
                </h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage candidate and campaign
                  information
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["Create", "Update", "Read", "Delete"].map((action) => (
                  <React.Fragment key={`candidate-${action}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Candidates`}</span>
                      <Checkbox
                        checked={isPermissionSelected(
                          `CandidateModel${action}`
                        )}
                        disabled
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Manifestos`}</span>
                      <Checkbox
                        checked={isPermissionSelected(
                          `ManifestoModel${action}`
                        )}
                        disabled
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Canvass */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">Canvass</h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage canvass
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["Create", "Update", "Read", "Delete"].map((action) => (
                  <React.Fragment key={`canvass-${action}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-small">{`${action} Canvass`}</span>
                      <Checkbox
                        checked={isPermissionSelected(`CanvassModel${action}`)}
                        disabled
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* System & Logs */}
            <div className="flex flex-col gap-2 p-4 rounded-sm bg-secondary">
              <div className="mb-4">
                <h1 className="mb-2 font-bold text-medium">System & Logs</h1>
                <p className="text-small text-lighterGrey">
                  Permission holder can manage system logs
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["Create", "Update", "Read", "Delete"].map((action) => (
                  <div
                    key={`logs-${action}`}
                    className="flex items-center justify-between">
                    <span className="font-bold text-small">{`${action} Activity Logs`}</span>
                    <Checkbox
                      checked={isPermissionSelected(
                        `ActivityLogModel${action}`
                      )}
                      disabled
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mt-8">
              <Button
                text="Back"
                onClick={handleBack}
                customTextColor="primary"
                customBgColor="secondary"
                className="h-[70px] flex-1"
              />
              <Button
                text={isEditMode ? "Update Role" : "Create Role"}
                icon={<ArrowRightOutlined />}
                onClick={handleProceed}
                className="h-[70px] flex-1 bg-primary text-white"
              />
            </div>
          </div>
        </div>
      </div>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header={isEditMode ? "Role Updated" : "Role Added"}
        paragraph={
          isEditMode
            ? "You have successfully updated this role."
            : "You have successfully added a new role."
        }
        buttonText="Close"
        onButtonClick={handleCloseSuccess}
      />
    </div>
  );
};

export default AddRolesStep3;
